import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useDateFilter } from "@/context/DateFilterContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Archive, Trash2, Target, User, Calendar as Cal, X } from "lucide-react";
import { GOAL_STATUS, QUARTERS, STATUS_COLORS } from "@/lib/constants";
import { useSettings } from "@/context/SettingsContext";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { toast } from "sonner";

const empty = { title: "", brand_target: "Both", category: "New Service Launch", quarter: "Q1", progress: 0, status: "On Track", owner: "", start_date: "", target_date: "", milestones: [] };

export default function Goals() {
  const { start, end } = useDateFilter();
  const { settings } = useSettings();
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [milestoneText, setMilestoneText] = useState("");

  const load = () => api.get("/goals", { params: { start, end } }).then((r) => setGoals(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (g) => { setForm({ ...empty, ...g, start_date: (g.start_date || "").slice(0, 10), target_date: (g.target_date || "").slice(0, 10) }); setEditId(g.id); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      const payload = { ...form, start_date: form.start_date || null, target_date: form.target_date || null };
      if (editId) await api.put(`/goals/${editId}`, payload);
      else await api.post("/goals", payload);
      setOpen(false); load();
      toast.success(editId ? "Goal updated" : "Goal created");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const archive = async (id) => { await api.delete(`/goals/${id}`); load(); toast.success("Goal archived"); };
  const del = async (id) => { await api.delete(`/goals/${id}`, { params: { permanent: true } }); load(); toast.success("Goal deleted"); };

  const addMilestone = () => {
    if (!milestoneText.trim()) return;
    setForm({ ...form, milestones: [...form.milestones, { id: crypto.randomUUID(), text: milestoneText, done: false }] });
    setMilestoneText("");
  };
  const toggleMilestone = (mid) => {
    const ms = form.milestones.map((x) => x.id === mid ? { ...x, done: !x.done } : x);
    const done = ms.filter((x) => x.done).length;
    setForm({ ...form, milestones: ms, progress: ms.length ? Math.round((done / ms.length) * 100) : form.progress });
  };
  const removeMilestone = (mid) => setForm({ ...form, milestones: form.milestones.filter((x) => x.id !== mid) });

  return (
    <div className="space-y-6" data-testid="goals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Goals & OKRs</h1>
          <p className="text-muted-foreground">Departmental innovation targets, milestones & velocity.</p>
        </div>
        <Button onClick={openNew} data-testid="new-goal-btn"><Plus size={16} className="mr-1" /> New Goal</Button>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {goals.length === 0 && <p className="text-muted-foreground">No goals in this period.</p>}
        {goals.map((g) => {
          const done = g.milestones?.filter((m) => m.done).length || 0;
          return (
            <Card key={g.id} className="flex flex-col p-5 border-slate-200 shadow-sm transition-transform hover:-translate-y-0.5" data-testid={`goal-card-${g.id}`}>
              <div className="flex items-start justify-between gap-2">
                <Badge variant="outline" className="border-secondary/40 text-secondary">{g.quarter}</Badge>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(g)} className="text-slate-400 hover:text-primary" data-testid={`edit-goal-${g.id}`}><Pencil size={15} /></button>
                  <button onClick={() => archive(g.id)} className="text-slate-400 hover:text-amber-600" data-testid={`archive-goal-${g.id}`}><Archive size={15} /></button>
                  <ConfirmDelete onConfirm={() => del(g.id)} name={g.title} testid={`delete-goal-${g.id}`} />
                </div>
              </div>
              <h3 className="mt-3 font-head font-bold leading-snug text-slate-900">{g.title}</h3>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Target size={12} /> {g.category}</span>
                <span className="inline-flex items-center gap-1"><User size={12} /> {g.owner || "Unassigned"}</span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{g.brand_target}</div>
              <div className="mt-4">
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className={`rounded-full border px-2 py-0.5 font-medium ${STATUS_COLORS[g.status]}`}>{g.status}</span>
                  <span className="font-semibold text-slate-700" data-testid={`goal-progress-value-${g.id}`}>{g.progress}%</span>
                </div>
                <Progress value={g.progress} className="h-2.5 transition-all" data-testid={`goal-progress-${g.id}`} />
              </div>
              {(g.start_date || g.target_date) && (
                <div className="mt-3 flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Cal size={12} /> {(g.start_date || "").slice(0, 10) || "—"} → {(g.target_date || "").slice(0, 10) || "—"}
                </div>
              )}
              {g.milestones?.length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <p className="mb-1 text-[11px] font-medium text-muted-foreground">Key Results · {done}/{g.milestones.length}</p>
                  <ul className="space-y-1">
                    {g.milestones.map((m) => (
                      <li key={m.id} className={`flex items-center gap-2 text-xs ${m.done ? "text-slate-400 line-through" : "text-slate-700"}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${m.done ? "bg-primary" : "bg-slate-300"}`} /> {m.text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="goal-modal">
          <DialogHeader><DialogTitle>{editId ? "Edit Goal" : "New Goal"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Launch 2 Anti-Aging Protocols in Q3" data-testid="goal-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Brand Target</Label>
                <Select value={form.brand_target} onValueChange={(v) => setForm({ ...form, brand_target: v })}>
                  <SelectTrigger data-testid="goal-brand-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{settings.brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Quarter</Label>
                <Select value={form.quarter} onValueChange={(v) => setForm({ ...form, quarter: v })}>
                  <SelectTrigger data-testid="goal-quarter-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{QUARTERS.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="goal-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{settings.goal_categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="goal-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{GOAL_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="goal-start-input" /></div>
              <div><Label>Target Date</Label><Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} data-testid="goal-target-input" /></div>
            </div>
            <div><Label>Owner / Lead</Label>
              <Select value={form.owner || "none"} onValueChange={(v) => setForm({ ...form, owner: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="goal-owner-select"><SelectValue placeholder="Select lead" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{Array.from(new Set([...(settings.researchers || []), form.owner].filter(Boolean))).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Progress: {form.progress}%</Label>
              <Slider value={[form.progress]} max={100} step={5} onValueChange={(v) => setForm({ ...form, progress: v[0] })} className="mt-2" data-testid="goal-progress-slider" />
            </div>
            <div>
              <Label>Key Results / Milestones</Label>
              <div className="mt-1 flex gap-2">
                <Input value={milestoneText} onChange={(e) => setMilestoneText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMilestone())} placeholder="Add a milestone..." data-testid="milestone-input" />
                <Button type="button" variant="secondary" onClick={addMilestone} data-testid="add-milestone-btn">Add</Button>
              </div>
              <ul className="mt-2 space-y-1">
                {form.milestones.map((m) => (
                  <li key={m.id} className="flex items-center gap-2 rounded-md bg-slate-50 px-2 py-1.5 text-sm">
                    <Checkbox checked={m.done} onCheckedChange={() => toggleMilestone(m.id)} data-testid={`milestone-check-${m.id}`} />
                    <span className={`flex-1 ${m.done ? "line-through text-slate-400" : ""}`}>{m.text}</span>
                    <button onClick={() => removeMilestone(m.id)} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-goal-btn">{editId ? "Save Changes" : "Create Goal"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
